import React from 'react';
import { ArrowLeft, Package, Truck, CreditCard, Calendar, MapPin, FileText, ExternalLink, Hash, Globe, Receipt } from 'lucide-react';
import type { Order, OrderItem } from '../../services/api';
import type { Creation } from '../../types/create';

interface OrderDetailViewProps {
  order: Order;
  selectedCreation: Creation | undefined | null;
  onBack: () => void;
}

export const OrderDetailView: React.FC<OrderDetailViewProps> = ({ order, selectedCreation, onBack }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const getProductDetails = (item: OrderItem) => {
    // Try to find product details from the creation's products
    if (selectedCreation?.products) {
      const product = selectedCreation.products.find(p => (p.gelatoProduct?.productUid || p.id) === item.productUid);
      if (product) {
        return {
          name: product.gelatoProduct?.title || product.name,
          image: item.files[0]?.url || product.image
        };
      }
    }
    return {
      name: 'Custom Product',
      image: item.files[0]?.url
    };
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Back to orders"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
                Order Details
            </h2>
            <p className="text-sm text-gray-500 font-mono">{order.id}</p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-gray-500">
            <Package className="w-4 h-4" />
            <span className="text-sm font-medium">Fulfillment Status</span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-lg font-medium capitalize ${
                order.fulfillmentStatus === 'shipped' ? 'text-green-600' :
                order.fulfillmentStatus === 'canceled' ? 'text-red-600' :
                'text-blue-600'
            }`}>
                {order.fulfillmentStatus ? order.fulfillmentStatus.replace(/_/g, ' ') : 'Processing'}
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2 text-gray-500">
            <CreditCard className="w-4 h-4" />
            <span className="text-sm font-medium">Financial Status</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-lg font-medium capitalize text-gray-900">
                {order.financialStatus ? order.financialStatus.replace(/_/g, ' ') : 'N/A'}
            </span>
            {order.currency && (
                <span className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-600">
                    {order.currency}
                </span>
            )}
          </div>
        </div>
      </div>

      {/* Shipment Info */}
      {order.shipment && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-gray-500" />
            Shipment Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {order.shipment.carrierName && (
                 <div>
                     <p className="text-sm text-gray-500">Carrier</p>
                     <p className="font-medium">{order.shipment.carrierName}</p>
                     {order.shipment.serviceType && (
                         <p className="text-sm text-gray-400">{order.shipment.serviceType}</p>
                     )}
                 </div>
             )}
             {order.shipment.trackingCode && (
                 <div>
                     <p className="text-sm text-gray-500">Tracking Code</p>
                     <div className="flex items-center gap-2">
                        <p className="font-mono">{order.shipment.trackingCode}</p>
                        {order.shipment.trackingUrl && (
                            <a 
                                href={order.shipment.trackingUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                            >
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        )}
                     </div>
                 </div>
             )}
          </div>
        </div>
      )}

      {/* Dates & Refs */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
         <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            Timeline & References
         </h3>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
            <div>
                <p className="text-gray-500">Created At</p>
                <p>{formatDate(order.createdAtServer || order.orderedAt)}</p>
            </div>
            {order.updatedAt && (
                <div>
                    <p className="text-gray-500">Last Updated</p>
                    <p>{formatDate(order.updatedAt)}</p>
                </div>
            )}
            <div>
                <p className="text-gray-500">Order Ref ID</p>
                <p className="font-mono text-gray-700">{order.orderReferenceId}</p>
            </div>
            {order.customerReferenceId && (
                <div>
                    <p className="text-gray-500">Customer Ref ID</p>
                    <p className="font-mono text-gray-700">{order.customerReferenceId}</p>
                </div>
            )}
            {order.channel && (
                <div>
                    <p className="text-gray-500">Channel</p>
                    <p className="capitalize">{order.channel}</p>
                </div>
            )}
         </div>
      </div>

      {/* Items */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
         <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Order Items
         </h3>
         <div className="space-y-4">
            {order.items.map((item, idx) => {
               const { name, image } = getProductDetails(item);
               return (
                   <div key={idx} className="flex gap-4 border-b border-gray-50 last:border-0 pb-4 last:pb-0">
                       <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                           {image ? (
                               <img src={image} alt={name} className="w-full h-full object-cover" />
                           ) : (
                               <div className="w-full h-full flex items-center justify-center text-gray-300">
                                   <Package className="w-6 h-6" />
                               </div>
                           )}
                       </div>
                       <div className="flex-1">
                           <p className="font-medium text-gray-900">{name}</p>
                           <p className="text-xs text-gray-500 font-mono mt-1">{item.productUid}</p>
                           <div className="flex gap-4 mt-2 text-sm">
                               <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">Qty: {item.quantity}</span>
                               <span className="text-gray-500">Ref: {item.itemReferenceId}</span>
                           </div>
                       </div>
                   </div>
               );
            })}
         </div>
      </div>

      {/* Addresses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         {order.shippingAddress && (
             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                 <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    Shipping Address
                 </h3>
                 <div className="text-sm text-gray-600 space-y-1">
                    <p className="font-medium text-gray-900">
                        {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                    </p>
                    {order.shippingAddress.companyName && <p>{order.shippingAddress.companyName}</p>}
                    <p>{order.shippingAddress.addressLine1}</p>
                    {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                    <p>
                        {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postCode}
                    </p>
                    <p>{order.shippingAddress.country}</p>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        {order.shippingAddress.email && <p>Email: {order.shippingAddress.email}</p>}
                        {order.shippingAddress.phone && <p>Phone: {order.shippingAddress.phone}</p>}
                    </div>
                 </div>
             </div>
         )}

         {order.returnAddress && (
             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                 <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    Return Address
                 </h3>
                 <div className="text-sm text-gray-600 space-y-1">
                    {/* Return address structure might vary, assume similar to shipping or a string */}
                    {typeof order.returnAddress === 'string' ? (
                        <p>{order.returnAddress}</p>
                    ) : (
                        <>
                           {order.returnAddress.companyName && <p className="font-medium">{order.returnAddress.companyName}</p>}
                           {order.returnAddress.addressLine1 && <p>{order.returnAddress.addressLine1}</p>}
                           {order.returnAddress.addressLine2 && <p>{order.returnAddress.addressLine2}</p>}
                           <p>
                               {[order.returnAddress.city, order.returnAddress.state, order.returnAddress.postCode].filter(Boolean).join(', ')}
                           </p>
                           {order.returnAddress.country && <p>{order.returnAddress.country}</p>}
                           {order.returnAddress.email && <p className="mt-2">Email: {order.returnAddress.email}</p>}
                        </>
                    )}
                 </div>
             </div>
         )}
      </div>

      {/* Receipts */}
      {order.receipts && order.receipts.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-gray-500" />
                Receipts
              </h3>
              <div className="space-y-3">
                  {order.receipts.map((receipt, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded-lg">
                          <span className="font-mono text-gray-600">{receipt.number || receipt.id || `Receipt #${idx + 1}`}</span>
                          <span className="text-gray-500">{receipt.type}</span>
                          {/* Add download link if URL is available in receipt object */}
                      </div>
                  ))}
              </div>
          </div>
      )}
      
      {/* Billing Entity */}
      {order.billingEntity && (
           <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
               <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-gray-500" />
                  Billing Entity
               </h3>
               <div className="text-sm text-gray-600">
                    <p className="font-medium text-gray-900">
                        {order.billingEntity.companyName || `${order.billingEntity.firstName || ''} ${order.billingEntity.lastName || ''}`}
                    </p>
                    <p>{order.billingEntity.addressLine1}</p>
                    <p>{order.billingEntity.city}, {order.billingEntity.country}</p>
                    <p>VAT: {order.billingEntity.vatNumber || 'N/A'}</p>
               </div>
           </div>
      )}

      {/* Metadata */}
      {order.metadata && order.metadata.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                  <Hash className="w-3 h-3" />
                  Metadata
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {order.metadata.map((meta, idx) => (
                      <div key={idx} className="flex text-xs">
                          <span className="font-medium text-gray-600 min-w-[120px]">{meta.key}:</span>
                          <span className="text-gray-500 truncate" title={meta.value}>{meta.value}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}

    </div>
  );
};

